import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import usePortal from 'react-useportal'
import { Logo, UserMenu, GenericButton, Page } from '../components'
import {
  IonButtons,
  IonList,
  IonAlert,
  useIonViewWillLeave,
  useIonViewDidEnter,
} from '@ionic/react'
import { chevronBack, arrowForward } from 'ionicons/icons'
import {
  WorkflowStep,
  WorkflowContextConsumer,
  WorkflowContextProvider,
  WorkflowTrack,
} from './WorkflowContext'
import { renderDataFields } from '../util/data-field'
import { WorkflowButton } from '.'
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import { useRouter } from '../hooks'

export interface WorkflowProps extends React.ComponentProps<any> {
  id: string
  steps?: WorkflowStep[]
  tracks?: WorkflowTrack[]
  data?: { [key: string]: any }
  onComplete?: (data: { [key: string]: any }) => any
  parentComponent: any
  required?: boolean
  cancelUrl?: string
  startIndex?: number
  autosave?: boolean
}

const Workflow: React.FC<WorkflowProps> = ({
  steps = [],
  tracks,
  data = {},
  cancelUrl = '/a/dashboard/',
  onComplete,
  parentComponent,
  required,
  startIndex,
  autosave,
  id,
  ...rest
}) => {
  const workflowButton = useRef<any>()
  const { router, replaceURL } = useRouter()
  const [isOpen, setIsOpen] = useState(true)
  const { Portal } = usePortal({
    bindTo: document.getElementById('app') ?? document.body,
  })

  const Parent = parentComponent ?? Page

  useIonViewWillLeave(() => {
    setIsOpen(false)
  })

  useIonViewDidEnter(() => {
    setIsOpen(true)
  })

  return (
    <Parent>
      {isOpen && (
        <Portal>
          <WorkflowContextProvider
            initialState={{ steps, tracks, data, autosave, id }}
            initialStepIndex={startIndex}
          >
            <WorkflowContextConsumer>
              {({ state, dispatch }) => {
                let content
                const button = (
                  <WorkflowButton
                    onWorkflowComplete={onComplete}
                    icon={arrowForward}
                    ref={workflowButton}
                    {...(state.currentStep?.button ?? { children: 'Continue' })}
                  />
                )

                if (state.currentStep) {
                  const Component = state.currentStep.customComponent
                  content = (
                    <div className="step">
                      {state.currentStep.title && (
                        <h1 className="heading">{state.currentStep.title}</h1>
                      )}
                      {state.currentStep.subtext && (
                        <p className="subtext">{state.currentStep.subtext}</p>
                      )}
                      {Component ? (
                        <Component />
                      ) : (
                        <IonList lines="full">
                          {renderDataFields(
                            true,
                            [
                              ...(typeof state.currentStep.fields === 'function'
                                ? state.currentStep.fields(state.data)
                                : state.currentStep.fields ?? []
                              ).map((it) => ({
                                ...it,
                                onEnterPress: (key: string, val: any) => {
                                  dispatch({
                                    type: 'setData',
                                    value: {
                                      [key]: val,
                                    },
                                  })
                                  setTimeout(
                                    () => workflowButton.current?.click?.(),
                                    50
                                  )
                                },
                              })),
                            ],
                            state.data,
                            (key: string) => (val: any) =>
                              dispatch({
                                type: 'setData',
                                value: {
                                  [key]: val,
                                },
                              })
                          )}
                        </IonList>
                      )}
                      {state.currentStep.footnote && (
                        <p className="footnote">{state.currentStep.footnote}</p>
                      )}
                      {button}
                    </div>
                  )
                }

                return (
                  <div {...rest}>
                    <header className="workflow-header">
                      <IonButtons className="back-button">
                        <GenericButton
                          color="light"
                          fill="clear"
                          size="large"
                          shape="round"
                          visible={!required && state.history.length === 0}
                          onClick={() => {
                            dispatch({
                              type: 'clearSavedState',
                              value: {},
                            })

                            router.canGoBack()
                              ? router.back()
                              : replaceURL(cancelUrl)
                          }}
                        >
                          Cancel
                        </GenericButton>
                        <GenericButton
                          icon={chevronBack}
                          color="light"
                          fill="clear"
                          size="large"
                          shape="round"
                          visible={state.canGoBack}
                          onClick={() =>
                            dispatch({ type: 'goBack', value: {} })
                          }
                        >
                          Back
                        </GenericButton>
                      </IonButtons>
                      <Logo
                        className="logo"
                        type="horizontal"
                        depth="two"
                        color="black"
                        height={75}
                      />
                      <IonButtons className="user-menu">
                        <UserMenu arrowColor="light" />
                      </IonButtons>
                    </header>
                    <div className="content--container">
                      <div className="content">
                        <SwitchTransition>
                          <CSSTransition
                            key={state.transitionKey ?? 0}
                            classNames="workflow"
                            addEndListener={(node, done) =>
                              node.addEventListener(
                                'transitionend',
                                done,
                                false
                              )
                            }
                          >
                            {content}
                          </CSSTransition>
                        </SwitchTransition>
                      </div>
                    </div>
                    <IonAlert
                      isOpen={state.hasSavedState}
                      message="Do you want to continue where you left off?"
                      header="Continue?"
                      buttons={[
                        {
                          text: 'No',
                          handler() {
                            dispatch({
                              type: 'clearSavedState',
                              value: {},
                            })
                          },
                        },
                        {
                          text: 'Yes',
                          handler() {
                            dispatch({
                              type: 'restoreSavedState',
                              value: {},
                            })
                          },
                        },
                      ]}
                    />
                  </div>
                )
              }}
            </WorkflowContextConsumer>
          </WorkflowContextProvider>
        </Portal>
      )}
    </Parent>
  )
}

export default styled(React.memo(Workflow))`
  background: var(--primary-gradient);
  display: grid;
  grid-template-rows: auto 1fr;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
  max-height: 100vh;

  ion-content {
    --background: transparent;
  }

  .footnote {
    color: var(--ion-color-step-200);
    font-size: 12px;
    margin: 0 auto 20px;
    text-align: center;
  }

  .content--container {
    flex: 1 1 auto;
    overflow: auto;
  }

  .content {
    align-items: center;
    display: flex;
    flex-direction: column;
    color: white;
    justify-content: center;
    width: 100%;
    max-width: 1000px;
    min-height: 100%;
    margin: auto;
    text-align: center;

    .step {
      width: 100%;
    }

    ion-list {
      max-width: 400px;
      margin: 40px auto;
    }

    .heading {
      font-size: 50px;
    }

    .subtext {
      color: var(--ion-color-step-150);
      font-size: 14px;
    }
  }

  .back-button {
    margin-left: 10px;
  }

  .cancel-button {
    margin-left: 10px;
    &:not(.active) {
      visibility: hidden;
    }
  }

  .workflow-header {
    display: grid;
    grid-template-columns: 1fr auto 1fr;

    .logo {
      align-self: center;
      margin: 10px auto;

      @media screen and (max-width: 500px) {
        opacity: 0;
      }
    }

    .user-menu {
      justify-self: flex-end;
    }
  }
`
